import React, { useState, useEffect, useRef, useContext } from 'react'
import { FormattedDate } from 'react-intl'
import { FormattedMessage, useIntl } from 'react-intl'
import Icon from 'components/Icon'
import { Button } from '@arnsbogroup/react-voicearchive'

import Duration from './../Duration'
import CardListRadio from './../CardListRadio'
import Text from './../Text'
import VoiceTalent from './../VoiceTalent'
import Contact from './../Contact'
import Priority from './../Priority'
import TextArea from './../TextArea'
import Number from './../Number'
import DateTime from './../DateTime'
import PostProduction from './../PostProduction'
import Draft from './../Draft'
import LandingPageLink from '../LandingPageLink'
import DraftFutureOrder from './../DraftFutureOrder'
import AiVoiceLines from './../AiVoiceLines'
import VoiceTalentMulti from './../VoiceTalentMulti'
import Files from './../Files'
import Memories from './../Memories'
import Radio from './../Radio'
import CheckBox from './../CheckBox'
import messages from './messages'

const Hidden = () => null

const Components = {
  Duration,
  Text,
  TextArea,
  VoiceTalent,
  PostProduction,
  Draft,
  LandingPageLink,
  DraftFutureOrder,
  AiVoiceLines,
  Contact,
  Priority,
  Files,
  CardListRadio,
  VoiceTalentMulti,
  Hidden,
  Memories,
  DateTime,
  Number,
  Radio,
  CheckBox
}

const Template = ({ template, id, onUpserted, attributes }) => {
  const [interim, setInterim] = useState({})
  const [errors, setErrors] = useState(null)
  const [instance, setInstance] = useState(attributes || {})
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!template) return

    onTemplateChange(template)
  }, [template, attributes])

  const onTemplateChange = (template) => {
    setInstance({ 
      ...template.attributes.reduce((memo, attr) => {
        memo[attr.key] = attr.value || null
        return memo
      }, {}),
      ...attributes,
    })
  }

  const onSubmit = () => {
    on('upsert', onTemplateChange, onUpserted)
  }

  const onValidate = () => {
    on('validate', setInterim)
  }

  const on = (endpoint, callback, onAfterCallback = null) => {
    setLoading(true)

    let url = `/rest/v2/order`

    if (id) {
      url = `${url}/${id}`
    }

    url = `${url}/${endpoint}`

    if (template) {
      url = `${url}?template=${template.meta.hashId}`
    }

    const body = new FormData()

    Object.entries(instance).map(([key, value]) => {
      if (Array.isArray(value)) {
        for (const item of value) {
          if (item instanceof File) {
            body.append(`${key}[]`, item)
          } else {
            body.append(`${key}[]`, JSON.stringify(item))
          }
        }
      } else if (value instanceof String) {
        body.append(key, value)
      } else {
        body.append(key, JSON.stringify(value))
      }
    })

    const promise = fetch(url, { credentials: 'same-origin', method: 'POST', body })
      .then((response) => {
        setLoading(false)

        return response.json()
      })
      .then((response) => {
        if (response.errors) {
          setErrors(response.errors || [])
        } else {
          callback(response.data)
        }

        return response
      })

    if (onAfterCallback) {
      onAfterCallback(promise)
    }
  }

  if (!template) {
    return null
  }

  return (
    <div className="w-full h-full flex flex-col flex-wrap flex-1">
      { errors && Array.isArray(errors) && errors.filter((attribute, i) => !(attribute.key in errors)).map((error) => (
        <span className="rounded-sm bg-red-50 p-3 block">
          <div className="flex">
            <div className="shrink-0">
              <Icon icon="times-circle" className="text-red-800" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                { error }
              </h3>
            </div>
          </div>
        </span>
      )) }
      { template
          .attributes
          .filter(({ component }) => component in Components)
          .map((attribute, i) => (
            <div key={`${attribute.key}-${i}`} className="mb-6 px-2">
              { React.createElement(Components[attribute.component], {
                key: `${attribute.key}-${i}`,
                attribute,
                instance: instance,
                state: instance[attribute.key],
                interimState: interim[attribute.key] || null,
                onValidate,
                onChange: (change) => {
                  if (errors) {
                    const { [attribute.key]: _, ...nextErrors } = errors
                    setErrors(nextErrors)
                  }

                  setInstance({ ...instance, [attribute.key]: change })
                }
              }) }

              { errors && attribute.key in errors && errors[attribute.key].map((error) => (
                <span className="rounded-sm bg-red-50 p-3 block">
                  <div className="flex">
                    <div className="shrink-0">
                      <Icon icon="times-circle" className="text-red-800" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        { error }
                      </h3>
                    </div>
                  </div>
                </span>
              )) }
            </div>
          ))
      }

      <div className="flex items-center justify-end space-x-6 mb-6 px-2">
        { loading &&
          <span className="font-medium text-sm uppercase text-gray-700">
            Validating input.. <Icon icon="spinner-third" className="animate-spin" />
          </span>
        }

        <Button
          disabled={loading || template.meta.submitDisabled}
          onClick={onSubmit}
          icon={['fa', 'long-arrow-right']}
          iconPosition="right"
        >
          { id ? (
            <span><FormattedMessage {...messages.update}/></span>
          ) : (
            <span><FormattedMessage {...messages.submit}/></span>
          ) }
        </Button>
      </div>
    </div>
  )
}

export default Template
